import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { tap } from 'rxjs'
import { required, validationContext } from 'validx'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import {
  type FetchInventoryItemsByIdsFn,
  type ListInventoryItemsFn,
} from '@/features/inventory'
import { Bundle } from '@/features/admin/bundle/Bundle'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'
import {
  type WorkspaceSelection,
  type WorkspaceSelectorViewModel,
} from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import { ManageItemsInBundleViewModel } from '@/features/admin/bundle/components/ManageItemsInBundleForm/ManageItemsInBundleViewModel'
import { IncludedItemViewModel } from '@/features/admin/bundle/components/ManageItemsInBundleForm/IncludedItemViewModel'
import { PriceEditorViewModel } from '@/features/admin/bundle/PriceEditorViewModel'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { match } from '@/utils/pattern-match-util'
import { mustHaveMaxLength, mustHaveMinLength } from '@/utils/validx-util'

/**
 * What the bundle dialog does on submit.
 */
export type BundleDialogVariant = 'Create' | 'Edit' | 'Duplicate'

/**
 * View model for the dialog that creates, edits or duplicates a bundle.
 */
export class BundleDialogViewModel
  implements WorkspaceSelection, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly _fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
  } as const

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The name for the Bundle.
   */
  @observable
  accessor name = ''

  /**
   * The description for the Bundle.
   */
  @observable
  accessor description = ''

  /**
   * The included items for the Bundle.
   */
  @observable
  accessor includedItems: IncludedItemViewModel[] = []

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  accessor baseItemId = Id.none

  /**
   * The bundle being edited or duplicated; `Bundle.none` when creating one.
   */
  @observable
  accessor bundle: Bundle = Bundle.none

  /**
   * The selected workspace
   */
  @observable
  accessor workspace: Workspace | undefined = Workspace.none

  readonly manageItemsInBundleVm

  /**
   * The Bundle price editor view model.
   */
  readonly pricingVm = new PriceEditorViewModel()

  /**
   * Validation context for this view model.
   */
  readonly validation

  /**
   * Validations for the workspace
   */
  readonly workspaceValidation

  constructor(
    readonly variant: BundleDialogVariant,
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _fetchInventoryItemsByIds: FetchInventoryItemsByIdsFn,
    private readonly _bundleStore: BundleStore,
    private readonly _refreshBundles: RefreshBundlesFn,
    private readonly _getSelectedWorkspace: () => Workspace | undefined,
    readonly workspaceSelectorVm: WorkspaceSelectorViewModel,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.manageItemsInBundleVm = new ManageItemsInBundleViewModel(
      this.setIncludedItems,
      this._searchInventoryItems,
      this._createAlertRetryWrapper,
    )

    this.validation = validationContext(this, {
      name: [
        required('A name is required.'),
        mustHaveMinLength(
          this._fieldLengths.name.min,
          `Name must be between ${this._fieldLengths.name.min} and ${this._fieldLengths.name.max} characters.`,
        ),
        mustHaveMaxLength(
          this._fieldLengths.name.max,
          `Name must be between ${this._fieldLengths.name.min} and ${this._fieldLengths.name.max} characters.`,
        ),
      ],
      description: [
        required('A description is required.'),
        mustHaveMinLength(
          this._fieldLengths.description.min,
          `Description must be between ${this._fieldLengths.description.min} and ${this._fieldLengths.description.max} characters.`,
        ),
        mustHaveMaxLength(
          this._fieldLengths.description.max,
          `Description must be between ${this._fieldLengths.description.min} and ${this._fieldLengths.description.max} characters.`,
        ),
      ],
    })

    this.workspaceValidation = validationContext(this, {
      workspace: [(opts) => !!opts.value || 'Workspace is required'],
    })

    this._lifecycle.manage(this.manageItemsInBundleVm)

    if (this.variant === 'Duplicate') {
      this._lifecycle.manage(
        toStream(() => this.workspace).pipe(
          tap(() => this._refetchAssignedItems()),
        ),
      )
    }
  }

  /**
   * The included items, base item first and the rest ordered by name.
   */
  @computed
  get orderedIncludedItems(): IncludedItemViewModel[] {
    const baseItem = this.includedItems.find((i) => i.isBaseItem)
    const allButBaseItem = this.includedItems
      .filter((i) => !i.isBaseItem)
      .sort((a, b) => a.name.localeCompare(b.name))

    if (baseItem) {
      return [baseItem, ...allButBaseItem]
    }

    return allButBaseItem
  }

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      `${max - current} characters remaining`

    return {
      name: makeIndicator(this._fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this._fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * The archived items count the footer warns about, if any.
   */
  @computed
  get archivedItemsCount() {
    return this.variant === 'Edit' && this.bundle.containsArchivedItems
      ? this.bundle.archivedItemsCount
      : undefined
  }

  /**
   * Set the workspace for the bundle
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.manageItemsInBundleVm.setWorkspaceId(this.workspace?.id)

    if (this.variant !== 'Duplicate') {
      return
    }

    const filteredIncludedItems = this.includedItems.filter(
      (item) =>
        item.workspaceId === undefined ||
        item.workspaceId === this.workspace?.id,
    )

    this.manageItemsInBundleVm.setBaseItemId(
      filteredIncludedItems.find((i) => i.isBaseItem)?.inventoryItemId ??
        Id.none,
    )
    this.manageItemsInBundleVm.setCurrentlyIncludedItems(filteredIncludedItems)
  }

  /**
   * Resets the form.
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.includedItems = []
    this.baseItemId = Id.none
    this.manageItemsInBundleVm.setBaseItemId(Id.none)
    this.manageItemsInBundleVm.setCurrentlyIncludedItems([])

    this.submit.reset()
    this.validation.reset()
    this.pricingVm.reset()
  }

  /**
   * Opens the dialog for a new bundle in the selected workspace.
   */
  @action.bound
  open() {
    this.reset()
    this.setWorkspace(this._getSelectedWorkspace())
    this.modal.show()
  }

  /**
   * Opens the dialog prefilled with the bundle.
   */
  @action.bound
  openBundle(bundle: Bundle) {
    this.reset()
    this.setWorkspace(bundle.workspace)
    this._setBundle(bundle)
    this.modal.show()
  }

  @action.bound
  setIncludedItems(includedItems: IncludedItemViewModel[]) {
    this.includedItems = includedItems
    this.baseItemId =
      includedItems.find((item) => item.isBaseItem)?.inventoryItemId ?? Id.none
    this.manageItemsInBundleVm.setBaseItemId(this.baseItemId)

    if (this.variant === 'Edit') {
      this.manageItemsInBundleVm.setCurrentlyIncludedItems(this.includedItems)
    }
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingVm.validate()

    return base && pricing
  }

  /**
   * Creates, edits or duplicates the bundle.
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid ||
      this.workspace === undefined
    ) {
      return
    }

    if (!Id.some(this.baseItemId)) {
      this._newAlert()
        .withState('warning')
        .saying(
          'Base item is required',
          match(this.variant, {
            Create: () => 'Please include a base item.',
            Edit: () =>
              'Please mark the previously assigned base item or assign a different base item.',
            Duplicate: () => 'Please include a base item.',
          }),
        )
        .autoDismiss()
        .show()
      return
    }

    const baseItemId = this.baseItemId
    const params = {
      name: this.name,
      description: this.description,
      price: this.pricingVm.toValue(),
      baseItemId,
      includedItems: this.includedItems.filter(
        (item) =>
          !item.isBaseItem &&
          item.inventoryItemId !== baseItemId &&
          (this.variant !== 'Edit' || item.includedQuantity > 0),
      ),
    }

    const save =
      this.variant === 'Edit'
        ? this._bundleStore.edit({ ...params, id: this.bundle.id })
        : this._bundleStore.create({
            ...params,
            workspaceId: this.workspace.id,
          })

    await save.catch(
      this._createAlertErrorHandler(
        match(this.variant, {
          Create: () => 'Creating the bundle failed',
          Edit: () => 'Editing the bundle failed',
          Duplicate: () => 'Duplicating the bundle failed',
        }),
      ),
    )

    await this._refreshBundles(this.workspace ?? Workspace.none)

    this.validation.reset()

    this._newAlert()
      .withState('success')
      .saying(
        'Success',
        match(this.variant, {
          Create: () => 'Bundle created.',
          Edit: () => 'Bundle edited.',
          Duplicate: () => 'Bundle duplicated.',
        }),
      )
      .autoDismiss()
      .show()

    this.modal.close()
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  private _setBundle(bundle: Bundle) {
    this.bundle = bundle
    this.name = bundle.name
    this.description = bundle.description
    this.includedItems = bundle.includedItems.items.map(
      (i) =>
        new IncludedItemViewModel({
          isIncludedItem: true,
          isBaseItem: i.isBaseItem,
          inventoryItemId: i.inventoryItemId,
          inventoryItemVersionId: i.inventoryItemVersionId,
          name: i.name,
          price: i.price,
          proPrice: i.proPrice,
          description: i.description,
          workspaceId: i.workspaceId,
          includedQuantity: i.includedQuantity,
          archived: i.archived,
        }),
    )
    this.baseItemId =
      this.includedItems.find((item) => item.isBaseItem)?.inventoryItemId ??
      Id.none

    this.manageItemsInBundleVm.setBaseItemId(this.baseItemId)
    this.manageItemsInBundleVm.setCurrentlyIncludedItems(this.includedItems)

    this.pricingVm.hydrate(bundle.price)
  }

  /**
   * Re-checks the original items against the target workspace, since items
   * from another workspace can't be duplicated into it.
   */
  private _refetchAssignedItems() {
    this.manageItemsInBundleVm.setBaseItemId(
      this.bundle.includedItems.items.find((i) => i.isBaseItem)
        ?.inventoryItemId ?? Id.none,
    )
    // The retry alert has reported a failure; the items stay as they were.
    this._refetchAssignedIncludedItemsForDuplicating(
      this._lifecycle.signal,
    ).catch(() => undefined)
  }

  private async _refetchAssignedIncludedItemsForDuplicating(
    signal: AbortSignal,
  ) {
    if (!this.workspace) {
      return
    }

    const includedItemIds = this.includedItems.map(
      (item) => item.inventoryItemId,
    )
    const workspaceId = this.workspace.id

    const withRetry = this._createAlertRetryWrapper(
      'Loading the bundle items failed',
      signal,
    )
    const includedItems = await withRetry(() =>
      this._fetchInventoryItemsByIds(includedItemIds, workspaceId, signal),
    )

    const filteredIncludedItems = includedItems.filter(
      (item) => !item.workspace || item.workspace.id === workspaceId,
    )

    // Some items won't be added to the new bundle because they are part of a different workspace
    if (filteredIncludedItems.length !== includedItemIds.length) {
      this._newAlert()
        .withState('warning')
        .saying(
          'Some service items could not be included in the duplicated bundle',
          'Please create the missing service items and add to bundle.',
        )
        .autoDismiss()
        .show()
    }

    const includedItemVms = filteredIncludedItems.map(
      (item) =>
        new IncludedItemViewModel({
          isIncludedItem:
            this.includedItems.find((i) => i.inventoryItemId === item.id)
              ?.isIncludedItem || false,
          isBaseItem: item.id === this.baseItemId,
          inventoryItemId: item.id,
          inventoryItemVersionId: item.versionId,
          name: item.name,
          price: item.price,
          proPrice: item.proPrice,
          description: item.description,
          workspaceId: item.workspace?.id,
          includedQuantity:
            this.includedItems.find((i) => i.inventoryItemId === item.id)
              ?.includedQuantity ?? 0,
          archived: item.archived,
        }),
    )

    this.setIncludedItems(includedItemVms)
  }
}
